import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatTRY, formatDate, daysUntilLabel, daysUntil, productLabel } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Fuel as FuelIcon, Check, Trash2, Clock, Info } from "lucide-react";
import AytemizPanel from "@/components/AytemizPanel";

const empty = { invoice_no: "", product: "motorin", liters: "", unit_price: "", amount: "", invoice_date: new Date().toISOString().slice(0, 10), term_days: 14, note: "" };
const terms = [7, 10, 14, 21, 30, 45];

export default function Fuel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/fuel").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const recalcAmount = (liters, price) => {
    const l = parseFloat(liters), p = parseFloat(price);
    if (!isNaN(l) && !isNaN(p)) return (l * p).toFixed(2);
    return form.amount;
  };

  const save = async () => {
    if (!form.invoice_no || !form.amount) { toast.error("İrsaliye no ve tutar zorunlu"); return; }
    const payload = {
      invoice_no: form.invoice_no, product: form.product,
      liters: parseFloat(form.liters) || 0, unit_price: parseFloat(form.unit_price) || 0,
      amount: parseFloat(form.amount) || 0, invoice_date: form.invoice_date,
      term_days: parseInt(form.term_days) || 14, note: form.note,
    };
    try {
      if (editId) await api.put(`/fuel/${editId}`, payload);
      else await api.post("/fuel", payload);
      toast.success(editId ? "Fatura güncellendi" : "Yakıt alımı eklendi, ödeme planına işlendi");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const toggle = async (id) => { await api.patch(`/fuel/${id}/status`); load(); };
  const del = async (id) => { await api.delete(`/fuel/${id}`); toast.success("Silindi"); load(); };
  const openEdit = (it) => {
    setEditId(it.id);
    setForm({ ...it, liters: it.liters, unit_price: it.unit_price, amount: it.amount });
    setOpen(true);
  };

  const pending = items.filter((i) => i.status !== "odendi");
  const totalPending = pending.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <PageHeader
        testid="fuel-header"
        title="Aytemiz & Yakıt Vade Takvimi"
        subtitle="İrsaliye tarihine göre DBS / teminat vadesi otomatik hesaplanır ve ödeme planına işlenir."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <button data-testid="btn-add-fuel-invoice" className="px-4 py-2.5 rounded-xl bg-[#E65100] hover:bg-[#F57C00] transition-colors font-semibold text-sm flex items-center gap-2 shrink-0">
                <Plus className="w-4 h-4" /> Yakıt Alımı Ekle
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#161D2A] border-[#253043] text-[#F8FAFC]">
              <DialogHeader><DialogTitle className="font-display">{editId ? "Faturayı Düzenle" : "Yeni Yakıt Alımı"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="İrsaliye No" full>
                  <input data-testid="fuel-invoice-no-input" value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} className={inp} placeholder="IRS-2026-0000" />
                </Field>
                <Field label="Ürün">
                  <select data-testid="fuel-product-select" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className={inp}>
                    <option value="motorin">Motorin</option>
                    <option value="benzin">Kurşunsuz 95</option>
                    <option value="lpg">Otogaz / LPG</option>
                  </select>
                </Field>
                <Field label="İrsaliye Tarihi">
                  <input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className={inp} />
                </Field>
                <Field label="Litre">
                  <input type="number" value={form.liters} onChange={(e) => setForm({ ...form, liters: e.target.value, amount: recalcAmount(e.target.value, form.unit_price) })} className={inp} placeholder="0" />
                </Field>
                <Field label="Birim Fiyat (₺)">
                  <input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value, amount: recalcAmount(form.liters, e.target.value) })} className={inp} placeholder="0" />
                </Field>
                <Field label="Tutar (₺)" full>
                  <input data-testid="fuel-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inp} placeholder="0" />
                </Field>
                <Field label="Vade (gün)" full>
                  <div className="flex flex-wrap gap-2">
                    {terms.map((t) => (
                      <button key={t} onClick={() => setForm({ ...form, term_days: t })} className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${form.term_days === t ? "bg-[#E65100] border-[#E65100] text-white" : "bg-[#111620] border-[#253043] text-[#94A3B8]"}`}>
                        {t} gün
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
              <DialogFooter>
                <button data-testid="fuel-save-button" onClick={save} className="px-4 py-2.5 rounded-xl bg-[#E65100] hover:bg-[#F57C00] transition-colors font-semibold text-sm w-full">
                  {editId ? "Güncelle" : "Kaydet & Planla"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <AytemizPanel onImported={load} />

      <div className="card-surface p-5 mb-6 flex items-center gap-4 fade-up">
        <div className="w-11 h-11 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center">
          <FuelIcon className="w-5 h-5 text-[#F59E0B]" />
        </div>
        <div>
          <div className="text-sm text-[#94A3B8]">Toplam Bekleyen Yakıt Borcu</div>
          <div className="font-mono-num text-2xl font-bold" data-testid="fuel-total-pending">{formatTRY(totalPending)}</div>
        </div>
      </div>

      <div className="card-surface overflow-hidden fade-up">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-xs text-[#94A3B8] border-b border-[#253043] uppercase tracking-wide">
          <div className="col-span-3">İrsaliye / Ürün</div>
          <div className="col-span-2">İrsaliye Tarihi</div>
          <div className="col-span-2">Vade</div>
          <div className="col-span-2 text-right">Tutar</div>
          <div className="col-span-3 text-right">Durum / İşlem</div>
        </div>
        {items.length === 0 && <div className="p-10 text-center text-[#94A3B8] text-sm">Henüz yakıt alımı eklenmedi.</div>}
        {items.map((it) => {
          const paid = it.status === "odendi";
          const dleft = daysUntil(it.due_date);
          return (
            <div key={it.id} data-testid="fuel-table-row" className="grid grid-cols-2 md:grid-cols-12 gap-3 px-5 py-4 border-b border-[#253043] items-center hover:bg-[#1D2637]/40 transition-colors">
              <div className="col-span-2 md:col-span-3">
                <button onClick={() => openEdit(it)} className="text-left">
                  <div className="font-medium text-sm">{it.invoice_no}</div>
                  <div className="text-xs text-[#94A3B8]">{productLabel(it.product)} · {it.liters ? `${it.liters.toLocaleString("tr-TR")} L` : "-"}</div>
                </button>
              </div>
              <div className="md:col-span-2 text-sm text-[#94A3B8]">{formatDate(it.invoice_date)}</div>
              <div className="md:col-span-2">
                <div className="text-sm">{formatDate(it.due_date)}</div>
                {!paid && <div className={`text-[11px] ${dleft < 0 ? "text-[#EF4444]" : dleft <= 3 ? "text-[#F59E0B]" : "text-[#94A3B8]"}`}>{daysUntilLabel(it.due_date)}</div>}
              </div>
              <div className="md:col-span-2 text-right font-mono-num font-semibold">{formatTRY(it.amount)}</div>
              <div className="col-span-2 md:col-span-3 flex items-center justify-end gap-2">
                <button onClick={() => toggle(it.id)} data-testid="fuel-toggle-status" className={`px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${paid ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
                  {paid ? <Check className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  {paid ? "Ödendi" : "Beklemede"}
                </button>
                <button onClick={() => del(it.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-start gap-2.5 text-xs text-[#94A3B8] bg-[#111620] rounded-xl p-4">
        <Info className="w-4 h-4 text-[#3B82F6] shrink-0 mt-0.5" />
        <span>Aytemiz ödemeleri yukarıdaki <b>Aytemiz Ödeme Verisi</b> panelinden aktarılır. Buradan ayrıca elle yakıt alımı da ekleyebilir, vadesi & durumunu yönetebilirsiniz.</span>
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl bg-[#111620] border border-[#253043] focus:border-[#E65100] outline-none transition-colors text-sm";
function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-xs text-[#94A3B8] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
